import { useEffect } from "react";

// Primary (canonical) origin for GolfStart. Non-www is the chosen host.
const CANONICAL_ORIGIN = "https://mygolfstart.com";

/**
 * Sets per-page SEO tags:
 *  - document.title           (if `title` passed)
 *  - <meta name="description">(if `description` passed)
 *  - <link rel="canonical">   (always — self-referencing on CANONICAL_ORIGIN)
 *
 * The canonical URL is derived from the current `pathname` (or `path` override)
 * so every page always resolves to `https://mygolfstart.com{path}` — never www.
 *
 * All three tags are restored to their previous values on unmount so SPA
 * navigation leaves nothing stale behind.
 */
export function useSeo({ title, description, path } = {}) {
  useEffect(() => {
    // --- title ---
    const prevTitle = document.title;
    if (title) document.title = title;

    // --- description ---
    let descTag = document.querySelector('meta[name="description"]');
    const prevDesc = descTag?.getAttribute("content") || null;
    if (description) {
      if (!descTag) {
        descTag = document.createElement("meta");
        descTag.setAttribute("name", "description");
        document.head.appendChild(descTag);
      }
      descTag.setAttribute("content", description);
    }

    // --- canonical (always) ---
    const resolvedPath =
      path ||
      (typeof window !== "undefined" ? window.location.pathname : "/");
    const canonicalHref = `${CANONICAL_ORIGIN}${resolvedPath}`;

    let canonicalTag = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonicalTag?.getAttribute("href") || null;
    if (!canonicalTag) {
      canonicalTag = document.createElement("link");
      canonicalTag.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute("href", canonicalHref);

    return () => {
      if (title) document.title = prevTitle;
      if (description && descTag && prevDesc !== null)
        descTag.setAttribute("content", prevDesc);
      if (canonicalTag && prevCanonical !== null)
        canonicalTag.setAttribute("href", prevCanonical);
    };
  }, [title, description, path]);
}

export { CANONICAL_ORIGIN };
